import { useSelectedPatient } from '@/hooks/patient.hooks';
import { useUpdateQuestionnaire } from '@/hooks/questionnaire.hooks';
import { useQuestionnaireNavigation } from '@/context/NavigationProvider';
import { PatientPageLayout } from '@/components/PatientPageLayout';
import { Button } from '@/components/ui/button';
import { ResponseDateField } from '@/components/ResponseDateField';
import { ResponseTimeField } from '@/components/ResponseTimeField';
import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/types/graph';
import { scrollToTop } from '@/util/scroll';
import { useInjectConsultation } from '@/hooks/useInjectConsultation';
import { isWebsiteDemoPractice } from '@/util/demoPractice';
import { changePage } from '@/reducers/graph';
import { HiOutlineExclamationCircle } from 'react-icons/hi';

const getInitialDateAndTime = (consultationDate: string | Date | undefined) => {
  if (!consultationDate) return { date: null, time: null };
  const date = new Date(consultationDate);
  const time = new Date(consultationDate);
  date.setHours(0, 0, 0, 0);
  return { date, time };
};

export const ConsultationTime = () => {
  const { data: patient } = useSelectedPatient();
  const { navigateNext, showErrors } = useQuestionnaireNavigation();
  const consultation = useSelector((state: RootState) => state.consultation);
  const isDemoPracticeRun = useSelector((state: RootState) =>
    isWebsiteDemoPractice(state.consultation.practice?.slug),
  );
  const mode = useSelector((state: RootState) => state.graph.mode);
  const injectConsultation = useInjectConsultation();
  const dispatch = useDispatch();
  const currentPage = useSelector(
    (state: RootState) => state.graph.currentPage,
  );
  const [errors, setErrors] = useState<{
    consultationDate: boolean;
    consultationTime: boolean;
  }>({
    consultationDate: false,
    consultationTime: false,
  });
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (currentPage !== 'INTRODUCTION') {
      dispatch(changePage('INTRODUCTION'));
    }
  }, [currentPage, dispatch]);

  useEffect(() => {
    if (showErrors) {
      setShowErrorMessage(true);
    }
  }, [showErrors]);

  const initialValues = useMemo(
    () => getInitialDateAndTime(consultation?.date),
    [consultation?.date],
  );

  const [consultationDate, setConsultationDate] = useState<Date | null>(
    initialValues.date ?? new Date(),
  );
  // Website demo: the consultation is "now" — prefill the time so a single
  // click validates the step
  const [consultationTime, setConsultationTime] = useState<Date | null>(
    initialValues.time ?? (isDemoPracticeRun ? new Date() : null),
  );

  const { updateQuestionnaire, isLoading: isLoadingUpdate } =
    useUpdateQuestionnaire({
      onSuccess: () => {
        navigateNext();
      },
      onError: (error) => {
        console.error(
          'Erreur lors de la sauvegarde de la consultation:',
          error,
        );
      },
    });

  const validateForm = (form: {
    consultationDate: Date | null;
    consultationTime: Date | null;
  }) => {
    const errors = {
      consultationDate: form.consultationDate === null,
      consultationTime: form.consultationTime === null,
    };
    return { valid: !Object.values(errors).some((value) => value), errors };
  };

  const handleValidateClick = () => {
    const validation = validateForm({
      consultationDate,
      consultationTime,
    });
    if (!validation.valid) {
      setErrors(validation.errors);
      setShowErrorMessage(true);
      return;
    }

    if (patient?.id && consultationDate && consultationTime) {
      const date = new Date(consultationDate);
      date.setHours(consultationTime.getHours());
      date.setMinutes(consultationTime.getMinutes());

      injectConsultation({ date });
      updateQuestionnaire();
    }
  };

  return (
    <PatientPageLayout
      columnClassName="w-full"
      title={
        <p className="text-2xl min-[23rem]:text-3.5xl font-semibold nxs:text-center">
          {mode === 'SALLE_ATTENTE' ? (
            <>
              A qu&apos;elle heure est prévue
              <br />
              votre consultation ?{' '}
            </>
          ) : (
            <>
              Quand est prévue <br /> {patient?.self ? 'votre' : 'la'}{' '}
              consultation ?
            </>
          )}
        </p>
      }
      footer={
        <div className="flex flex-col w-full items-center">
          <Button
            variant="ctaPrimary"
            size="lg"
            onClick={handleValidateClick}
            disabled={
              isLoadingUpdate ||
              !validateForm({ consultationDate, consultationTime }).valid
            }
          >
            Valider
          </Button>
        </div>
      }
    >
      <div className="flex flex-col py-4 gap-4 w-full h-full nsm:items-center h-full">
        <div className="flex flex-col w-full h-full gap-4 max-w-[342px] nsm:mt-8 ">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.02em] text-gray-400 mb-1">
              Date de la consultation
            </p>
            <ResponseDateField
              value={
                mode === 'SALLE_ATTENTE'
                  ? new Date().getTime()
                  : consultationDate?.getTime()
              }
              onChange={(value: number | null) => {
                setConsultationDate(value ? new Date(value) : null);
                setErrors({ ...errors, consultationDate: false });
                setShowErrorMessage(false);
              }}
              disabled={false}
              allowPastDates={false}
            />
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.02em] text-gray-400 mb-1">
              Heure du rendez-vous
            </p>
            <ResponseTimeField
              label="Heure du rendez-vous"
              value={consultationTime?.getTime()}
              onChange={(value: number | null) => {
                setConsultationTime(value ? new Date(value) : null);
                setErrors({ ...errors, consultationTime: false });
                setShowErrorMessage(false);
              }}
              isError={errors.consultationTime}
              disabled={false}
              showNow
            />
          </div>
        </div>
      </div>
      {showErrorMessage && (
        <div className="flex items-center justify-center px-6 nsm:px-25 mb-2">
          <div className="flex items-center bg-red-100 rounded-lg p-2 w-full">
            <HiOutlineExclamationCircle
              className="text-red-600 mr-1 stroke-3"
              size={20}
            />
            <span className="text-red-600 text-xs">
              Veuillez renseigner les informations manquantes
            </span>
          </div>
        </div>
      )}
    </PatientPageLayout>
  );
};
